/**
 * Legend Container Component
 * Unified container for all map legends with accordion sections
 */

import React, { useMemo } from 'react';
import { Card } from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { LegendContainerProvider } from './LegendContainerContext';
import { LegendSection } from './LegendSection';
import {
  ReportLegendContent,
  GPSLostLegendContent,
  WeatherLegendContent,
  CoverageLegendContent,
  ColorByLegendContent,
} from './sections';
import { useReportContext } from '../../Reports/ReportContext';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import { useWeatherLegend } from '../WeatherLegend';
import { useCoverage } from '../Coverage/CoverageContext';
import { useColorByPin } from '../ColorTargetsBy/ColorByPinContext';

interface LegendContainerProps {
  className?: string;
}

interface ActiveLegends {
  hasReport: boolean;
  hasGPSLost: boolean;
  hasWeather: boolean;
  hasCoverage: boolean;
  hasColorBy: boolean;
}

const LegendContainerInner: React.FC<LegendContainerProps> = ({ className }) => {
  const { isPinned: colorByPinned } = useColorByPin();
  
  // Get legend data from various contexts
  const { legend: reportLegend } = useReportContext();
  const { gpsLostLegend } = useGPSLost();
  const { showLegend: showWeatherLegend, legend: weatherLegend, activeLayerLabel } = useWeatherLegend();
  const { showCoverageLegend, coverageLegend } = useCoverage();

  // Determine which legends are active
  const activeLegends: ActiveLegends = useMemo(() => ({
    hasReport: !!(reportLegend && reportLegend.items.length > 0),
    hasGPSLost: !!gpsLostLegend,
    hasWeather: showWeatherLegend && !!weatherLegend && weatherLegend.length > 0,
    hasCoverage: showCoverageLegend,
    hasColorBy: colorByPinned,
  }), [reportLegend, gpsLostLegend, showWeatherLegend, weatherLegend, showCoverageLegend, colorByPinned]);

  // Count active legends
  const activeLegendCount = useMemo(() => {
    return Object.values(activeLegends).filter(Boolean).length;
  }, [activeLegends]);

  // If no legends are active, don't render anything
  if (activeLegendCount === 0) {
    return null;
  }

  return (
    <Card className={cn('h-fit w-fit min-w-[200px] max-w-[400px]', className)}>
      {/* Legend Sections */}
      <div className="divide-y divide-border">
        {activeLegends.hasColorBy && (
          <LegendSection type="colorBy" title="Vessel Colors">
            <ColorByLegendContent />
          </LegendSection>
        )}
        
        {activeLegends.hasReport && reportLegend && (
          <LegendSection 
            type="report" 
            title={reportLegend.title}
            subtitle={reportLegend.subtitle}
          >
            <ReportLegendContent legend={reportLegend} />
          </LegendSection>
        )}
        
        {activeLegends.hasGPSLost && gpsLostLegend && (
          <LegendSection 
            type="gpsLost" 
            title={gpsLostLegend.title}
            subtitle={gpsLostLegend.subtitle}
          >
            <GPSLostLegendContent legend={gpsLostLegend} />
          </LegendSection>
        )}
        
        {activeLegends.hasWeather && weatherLegend && (
          <LegendSection 
            type="weather" 
            title={activeLayerLabel || 'Weather'}
          >
            <WeatherLegendContent legend={weatherLegend} />
          </LegendSection>
        )}
        
        {activeLegends.hasCoverage && (
          <LegendSection 
            type="coverage" 
            title={coverageLegend.title}
            subtitle={coverageLegend.subtitle}
          >
            <CoverageLegendContent />
          </LegendSection>
        )}
      </div>
    </Card>
  );
};

export const LegendContainer: React.FC<LegendContainerProps> = (props) => {
  return (
    <LegendContainerProvider>
      <LegendContainerInner {...props} />
    </LegendContainerProvider>
  );
};

export default LegendContainer;
